// src/pages/RestaurantOrderPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import Breadcrumb from "../components/Breadcrumb/Breadcrumb";
import CheckOutInvoice from "../components/CheckOutInvoice";
import EditOrderModal from "./EditOrderModal";
import { useAuth } from "../context/AuthContext";

import {
  Typography,
  Tabs,
  Table,
  Tag,
  Space,
  Button,
  Modal,
  Input,
  message,
  notification,
  Card,
  Tooltip,
  Popconfirm,
  Dropdown,
  Alert,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
  TableOutlined,
  StopOutlined,
  DownOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;

/* ---------------- helpers ---------------- */
const asNum = (v) => Number(v ?? 0);
const money = (n, ccy = "BWP") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: ccy }).format(
    asNum(n)
  );
const pick = (obj, keys) => {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};

// Normalize an order object (handles PascalCase/camelCase)
const normalizeOrder = (o) => {
  if (!o) return null;
  return {
    orderId: pick(o, ["orderId", "OrderId"]),
    roomNumber: pick(o, ["roomNumber", "RoomNumber"]),
    description: pick(o, ["description", "Description"]) ?? "",
    amount: asNum(pick(o, ["amount", "Amount"])) || 0,
    createdDate: pick(o, ["createdDate", "CreatedDate"]),
    createdBy: pick(o, ["createdBy", "CreatedBy"]) ?? "System",
    status: pick(o, ["status", "Status"]) ?? "Pending",

    // fulfilment fields
    deliveryMethod:
      pick(o, ["deliveryMethod", "DeliveryMethod"]) || "RoomService", // RoomService | Collection | DineIn
    pickupLocation: pick(o, ["pickupLocation", "PickupLocation"]) || "",
    tableReference: pick(o, ["tableReference", "TableReference"]) || "",

    // decision fields
    rejectionReason:
      pick(o, ["rejectionReason", "RejectionReason", "rejectReason"]) || "",
    acceptedDate: pick(o, ["acceptedDate", "AcceptedDate"]),
    rejectedDate: pick(o, ["rejectedDate", "RejectedDate"]),
  };
};

const statusTag = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "pending")
    return <Tag icon={<ClockCircleOutlined />} color="gold">Pending</Tag>;
  if (s === "accepted")
    return <Tag icon={<CheckCircleOutlined />} color="blue">Accepted</Tag>;
  if (s === "processing" || s === "processed" || s === "in progress")
    return <Tag color="purple">Processing</Tag>;
  if (s === "rejected")
    return <Tag icon={<CloseCircleOutlined />} color="red">Rejected</Tag>;
  if (s === "completed" || s === "closed")
    return <Tag color="green">Completed</Tag>;
  return <Tag>{status}</Tag>;
};

const FulfilmentCell = ({ order }) => {
  const method = order.deliveryMethod;
  if (method === "Collection") {
    return (
      <Space direction="vertical" size={0}>
        <Tag>Collection</Tag>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {order.pickupLocation ? `Pickup @ ${order.pickupLocation}` : "Pickup @ counter"}
        </Text>
      </Space>
    );
  }
  if (method === "DineIn") {
    return (
      <Space direction="vertical" size={0}>
        <Tag color="orange">Dine-in</Tag>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {order.tableReference ? `Table: ${order.tableReference}` : "No table reserved yet"}
        </Text>
      </Space>
    );
  }
  // RoomService default
  return (
    <Space direction="vertical" size={0}>
      <Tag color="green">Room Service</Tag>
      {order.roomNumber ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Deliver to Room {order.roomNumber}
        </Text>
      ) : null}
    </Space>
  );
};

/* ---- AntD modals ---- */
const ReserveTableModal = ({
  open,
  order,
  tableRef,
  setTableRef,
  onCancel,
  onSubmit,
  submitting,
}) => {
  return (
    <Modal
      open={open}
      onCancel={onCancel}
      onOk={onSubmit}
      okText="Reserve Table"
      confirmLoading={submitting}
      title={<Space><TableOutlined /> <span>Reserve a Table</span></Space>}
    >
      <Space direction="vertical" style={{ width: "100%" }}>
        <Text type="secondary">
          Dine-in reservation for {order?.roomNumber ? `Room ${order.roomNumber}` : "guest"}.
        </Text>
        <Text strong>Table number / reference</Text>
        <Input
          placeholder="e.g. 5 / Window area"
          value={tableRef}
          onChange={(e) => setTableRef(e.target.value)}
          disabled={submitting}
        />
      </Space>
    </Modal>
  );
};

const RejectOrderModal = ({
  open,
  order,
  reason,
  setReason,
  onCancel,
  onSubmit,
  submitting,
}) => {
  return (
    <Modal
      open={open}
      onCancel={onCancel}
      onOk={onSubmit}
      okText="Reject Order"
      okButtonProps={{ danger: true }}
      confirmLoading={submitting}
      title={<Space><StopOutlined /> <span>Reject Order</span></Space>}
    >
      <Space direction="vertical" style={{ width: "100%" }}>
        <Text type="secondary">
          Provide a reason (e.g., restaurant closed, item unavailable).
        </Text>
        <Text strong>Reason</Text>
        <Input.TextArea
          rows={4}
          placeholder="Restaurant is closed at this time."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={submitting}
        />
      </Space>
    </Modal>
  );
};

const RestaurantOrderPage = () => {
  const { authData } = useAuth();
  const hotelId = authData?.hotelId;

  const [activeKey, setActiveKey] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");

  // cache per tab
  const [cache, setCache] = useState({
    pending: null,
    accepted: null,
    processing: null,
    rejected: null,
    completed: null,
  });

  // Edit modal
  const [isEditOrderModalOpen, setIsEditOrderModalOpen] = useState(false);
  const [orderToEdit, setOrderToEdit] = useState(null);

  // Reserve table
  const [isReserveOpen, setIsReserveOpen] = useState(false);
  const [orderToReserve, setOrderToReserve] = useState(null);
  const [reserveTableRef, setReserveTableRef] = useState("");
  const [reserveSubmitting, setReserveSubmitting] = useState(false);

  // Reject order
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [orderToReject, setOrderToReject] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectSubmitting, setRejectSubmitting] = useState(false);

  const [api, contextHolder] = notification.useNotification();

  const handleEditOrder = (order) => {
    setOrderToEdit(order);
    setIsEditOrderModalOpen(true);
  };

  const refreshTab = async (key = activeKey) => {
    if (!hotelId) {
      setErrorMsg("Hotel ID is missing. Please sign in again.");
      return;
    }

    setErrorMsg("");
    setLoading(true);
    try {
      let data = [];
      if (key === "pending") {
        const res = await axiosInstance.get(`/hotels/${hotelId}/RestaurantOrders/activeOrders`);
        data = (res.data || []).map(normalizeOrder);
      } else if (key === "completed") {
        const res = await axiosInstance.get(`/hotels/${hotelId}/RestaurantOrders/closedOrders`);
        data = (res.data || []).map(normalizeOrder);
      } else {
        // accepted / processing / rejected
        try {
          const res = await axiosInstance.get(
            `/hotels/${hotelId}/RestaurantOrders/byStatus?status=${encodeURIComponent(key)}`
          );
          data = (res.data || []).map(normalizeOrder);
        } catch {
          data = [];
          setErrorMsg(
            "Add: GET /api/hotels/{hotelId}/RestaurantOrders/byStatus?status=" + key
          );
        }
      }
      setOrders(data);
      setCache((prev) => ({ ...prev, [key]: data }));
    } catch (err) {
      console.error("Fetch failed:", err);
      setOrders([]);
      setErrorMsg(
        err?.response?.data?.message ||
          "Failed to fetch orders for this status. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const onTabChange = async (key) => {
    setActiveKey(key);
    if (cache[key] !== null) {
      setOrders(cache[key] || []);
      return;
    }
    await refreshTab(key);
  };

  useEffect(() => {
    if (hotelId) {
      refreshTab("pending");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId]);

  const handleCloseOrder = async (orderId) => {
    if (!hotelId) {
      message.error("Hotel ID is missing.");
      return;
    }
    try {
      await axiosInstance.post(`/hotels/${hotelId}/RestaurantOrders/CloseOrder/${orderId}`);
      message.success("Order closed.");
      await refreshTab();
      setCache((prev) => ({ ...prev, completed: null }));
    } catch (err) {
      console.error("Error closing order:", err);
      message.error("Failed to close order.");
    }
  };

  const handleAcceptOrder = async (orderId) => {
    if (!hotelId) {
      message.error("Hotel ID is missing.");
      return;
    }
    try {
      await axiosInstance.post(`/hotels/${hotelId}/RestaurantOrders/AcceptOrder/${orderId}`);
      message.success("Order accepted.");
      await refreshTab();
      setCache((prev) => ({ ...prev, accepted: null }));
    } catch (err) {
      console.error("Error accepting order:", err);
      const msg =
        err?.response?.data?.message ||
        (typeof err?.response?.data === "string" ? err.response.data : null) ||
        "Failed to accept order.";
      message.error(msg);
    }
  };

  // Reject order
  const openRejectModal = (order) => {
    setOrderToReject(order);
    setRejectReason("");
    setIsRejectOpen(true);
  };

  const submitReject = async () => {
    if (!orderToReject || !hotelId) {
      message.error("Hotel ID is missing.");
      return;
    }
    if (!rejectReason.trim()) {
      message.warning("Please enter a reason.");
      return;
    }
    try {
      setRejectSubmitting(true);
      await axiosInstance.post(
        `/hotels/${hotelId}/RestaurantOrders/RejectOrder/${orderToReject.orderId}`,
        { reason: rejectReason.trim() }
      );
      setIsRejectOpen(false);
      setOrderToReject(null);
      message.success("Order rejected.");
      await refreshTab();
      setCache((prev) => ({ ...prev, rejected: null }));
    } catch (err) {
      console.error("Reject order failed:", err);
      const msg =
        err?.response?.data?.message ||
        (typeof err?.response?.data === "string" ? err.response.data : null) ||
        "Failed to reject order.";
      message.error(msg);
    } finally {
      setRejectSubmitting(false);
    }
  };

  // Reserve table
  const openReserveModal = (order) => {
    setOrderToReserve(order);
    setReserveTableRef(order?.tableReference || "");
    setIsReserveOpen(true);
  };

  const submitReserve = async () => {
    if (!orderToReserve || !hotelId) {
      message.error("Hotel ID is missing.");
      return;
    }
    if (!reserveTableRef.trim()) {
      message.warning("Enter a table number/reference.");
      return;
    }
    try {
      setReserveSubmitting(true);
      await axiosInstance.post(
        `/hotels/${hotelId}/RestaurantOrders/ReserveTable/${orderToReserve.orderId}`,
        { tableReference: reserveTableRef.trim() }
      );
      setIsReserveOpen(false);
      setOrderToReserve(null);
      message.success("Table reserved.");
      await refreshTab();
    } catch (err) {
      console.error("Reserve table failed:", err);
      const msg =
        err?.response?.data?.message ||
        (typeof err?.response?.data === "string" ? err.response.data : null) ||
        "Failed to reserve table.";
      message.error(msg);
    } finally {
      setReserveSubmitting(false);
    }
  };

  // Generic status update (PUT /RestaurantOrders/{id}/status; body is raw string)
  const updateStatus = async (order, nextStatus) => {
    if (!hotelId) {
      message.error("Hotel ID is missing.");
      return;
    }
    try {
      await axiosInstance.put(
        `/hotels/${hotelId}/RestaurantOrders/${order.orderId}/status`,
        nextStatus,
        { headers: { "Content-Type": "application/json" } }
      );
      api.success({
        message: "Order Updated",
        description: `Order #${order.orderId} marked as ${nextStatus}.`,
        placement: "bottomRight",
      });
      await refreshTab();
      setCache((prev) => ({
        ...prev,
        accepted: null,
        processing: null,
        rejected: null,
        completed: null,
        pending: null,
      }));
    } catch (err) {
      console.error("Status update failed:", err);
      const msg =
        err?.response?.data?.message ||
        (typeof err?.response?.data === "string" ? err.response.data : null) ||
        "Failed to update status.";
      message.error(msg);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: "Order ID",
        dataIndex: "orderId",
        key: "orderId",
        width: 110,
        sorter: (a, b) => asNum(a.orderId) - asNum(b.orderId),
      },
      {
        title: "Room",
        dataIndex: "roomNumber",
        key: "roomNumber",
        width: 110,
        render: (v) => (v ? <Tag>Room {v}</Tag> : <Tag color="default">N/A</Tag>),
      },
      {
        title: "Fulfilment",
        key: "fulfilment",
        width: 200,
        render: (_, order) => <FulfilmentCell order={order} />,
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
        ellipsis: true,
      },
      {
        title: "Total",
        dataIndex: "amount",
        key: "amount",
        width: 120,
        render: (v) => <Text strong>{money(v, "BWP")}</Text>,
        sorter: (a, b) => asNum(a.amount) - asNum(b.amount),
      },
      {
        title: "Date Added",
        dataIndex: "createdDate",
        key: "createdDate",
        width: 180,
        render: (v) => (v ? new Date(v).toLocaleString() : ""),
        sorter: (a, b) => new Date(a.createdDate) - new Date(b.createdDate),
      },
      {
        title: "Processed By",
        dataIndex: "createdBy",
        key: "createdBy",
        width: 140,
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 140,
        render: (s, order) => (
          <Space direction="vertical" size={0}>
            {statusTag(s)}
            {s === "Rejected" && order.rejectionReason ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Reason: {order.rejectionReason}
              </Text>
            ) : null}
          </Space>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 380,
        render: (_, order) => {
          const isPending = order.status === "Pending";
          const canReserve =
            order.deliveryMethod === "DineIn" &&
            !order.tableReference &&
            order.status !== "Closed" &&
            order.status !== "Completed";

          const updateMenu = {
            items: [
              { key: "Accepted", label: "Mark as Accepted" },
              { key: "Processing", label: "Mark as Processing" },
              { key: "Ready", label: "Mark as Ready" },
              { key: "Completed", label: "Mark as Completed" },
              { key: "Rejected", label: "Mark as Rejected" },
            ],
            onClick: ({ key }) => updateStatus(order, key),
          };

          return (
            <Space wrap>
              <Tooltip title="Edit order">
                <Button icon={<EditOutlined />} onClick={() => handleEditOrder(order)}>
                  Edit
                </Button>
              </Tooltip>

              {isPending && (
                <>
                  <Button type="primary" onClick={() => handleAcceptOrder(order.orderId)}>
                    Accept
                  </Button>
                  <Button danger onClick={() => openRejectModal(order)}>
                    Reject
                  </Button>
                </>
              )}

              {canReserve && (
                <Button icon={<TableOutlined />} onClick={() => openReserveModal(order)}>
                  Reserve Table
                </Button>
              )}

              {(order.status === "Pending" || order.status === "Accepted") && (
                <Popconfirm
                  title="Close this order?"
                  okText="Yes"
                  cancelText="No"
                  onConfirm={() => handleCloseOrder(order.orderId)}
                >
                  <Button>Close</Button>
                </Popconfirm>
              )}

              <Dropdown menu={updateMenu} trigger={["click"]}>
                <Button>
                  Update Status <DownOutlined />
                </Button>
              </Dropdown>
            </Space>
          );
        },
      },
    ],
    [hotelId] // include hotelId dependency
  );

  const tabs = [
    { key: "pending", label: "Pending" },
    { key: "accepted", label: "Accepted" },
    { key: "processing", label: "Processing" },
    { key: "rejected", label: "Rejected" },
    { key: "completed", label: "Completed" },
  ];

  if (!hotelId) {
    return (
      <div style={{ padding: 16 }}>
        <Breadcrumb />
        <Alert
          type="error"
          showIcon
          message="Hotel ID Missing"
          description="Hotel ID was not found in auth data. Please sign in again."
          style={{ marginTop: 16 }}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      {contextHolder}
      <Breadcrumb />
      <Title level={3} style={{ marginTop: 8, marginBottom: 16 }}>
        Restaurant / Bar Orders
        <Tag color="blue" style={{ marginLeft: 8 }}>Hotel #{hotelId}</Tag>
      </Title>

      <Card bodyStyle={{ paddingTop: 0 }}>
        <Tabs
          activeKey={activeKey}
          onChange={onTabChange}
          items={tabs.map((t) => ({
            key: t.key,
            label: t.label,
            children: (
              <>
                {errorMsg ? (
                  <Card type="inner" style={{ marginBottom: 12 }} title="Heads up">
                    <Space>
                      <ExclamationCircleOutlined />
                      <Text type="secondary">{errorMsg}</Text>
                    </Space>
                  </Card>
                ) : null}

                <Table
                  rowKey="orderId"
                  dataSource={orders}
                  columns={columns}
                  loading={loading}
                  pagination={{ pageSize: 10, showSizeChanger: true }}
                />

                {/* Optional: sample invoice when on Pending */}
                {activeKey === "pending" && orders.length > 0 && (
                  <CheckOutInvoice
                    isOpen={false}
                    onClose={() => {}}
                    guestDetails={{ roomNumber: orders[0].roomNumber }}
                    hotelDetails={{
                      hotelName: "Restaurant Name",
                      address: "Restaurant Address",
                      phoneNumber: "+000000000",
                    }}
                    billingRecords={orders}
                  />
                )}
              </>
            ),
          }))}
        />
      </Card>

      {/* Edit Order Modal */}
      {isEditOrderModalOpen && orderToEdit && (
        <EditOrderModal
          isOpen={isEditOrderModalOpen}
          order={orderToEdit}
          hotelId={hotelId}
          onClose={() => setIsEditOrderModalOpen(false)}
          onOrderUpdated={() => refreshTab()}
        />
      )}

      {/* Reserve Table Modal */}
      <ReserveTableModal
        open={isReserveOpen}
        order={orderToReserve}
        tableRef={reserveTableRef}
        setTableRef={setReserveTableRef}
        onCancel={() => {
          setIsReserveOpen(false);
          setOrderToReserve(null);
          setReserveTableRef("");
        }}
        onSubmit={submitReserve}
        submitting={reserveSubmitting}
      />

      {/* Reject Order Modal */}
      <RejectOrderModal
        open={isRejectOpen}
        order={orderToReject}
        reason={rejectReason}
        setReason={setRejectReason}
        onCancel={() => {
          setIsRejectOpen(false);
          setOrderToReject(null);
          setRejectReason("");
        }}
        onSubmit={submitReject}
        submitting={rejectSubmitting}
      />
    </div>
  );
};

export default RestaurantOrderPage;